import React, { createContext, useContext, useEffect, useState, startTransition } from 'react';

type Theme = 'light' | 'dark' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  useEffect(() => {
    const root = window.document.documentElement;

    const style = document.createElement('style');
    style.setAttribute('data-theme-transition-disable', '');
    style.appendChild(document.createTextNode('*{transition: none !important;} *::before{transition:none !important;} *::after{transition:none !important;}'));
    document.head.appendChild(style);

    root.classList.remove('light', 'dark');

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const systemTheme: 'light' | 'dark' = mql.matches ? 'dark' : 'light';
    const effectiveTheme: 'light' | 'dark' = theme === 'system' ? systemTheme : theme;
    root.classList.add(effectiveTheme);



    requestAnimationFrame(() => {
      if (style.parentNode) {
        document.head.removeChild(style);
      }
    });

    if (theme === 'system') {
      const handler = (e: MediaQueryListEvent) => {
        const newSystemTheme: 'light' | 'dark' = e.matches ? 'dark' : 'light';
        const next = newSystemTheme;
        root.classList.remove('light', 'dark');
        root.classList.add(next);

      };
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
  }, [theme]);

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme);
      // Schedule as a non-urgent transition to keep UI responsive
      startTransition(() => {
        setTheme(newTheme);
      });
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};